import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Snackbar,
  Alert,
  Grid,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import withUserAuth from '../hoc/withUserAuth';
import { AxiosError } from 'axios';
import { Images } from '../assets';

const BRAND_RED = '#E31E25';
const LOGIN_LOGO_SIZE = 132;

const BRAND_TEXT_SX = {
  fontWeight: 700,
  fontSize: '1.75rem',
  lineHeight: 1.1,
  color: '#FFFFFF',
  letterSpacing: '0.04em',
  fontFamily: 'inherit',
  userSelect: 'none',
} as const;

const LoginBrand = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 2,
    }}
  >
    <Box
      component="img"
      src={Images.ACDOCTORLOGO}
      alt="AC Doctor"
      sx={{
        width: LOGIN_LOGO_SIZE,
        height: LOGIN_LOGO_SIZE,
        borderRadius: '50%',
        flexShrink: 0,
        display: 'block',
        objectFit: 'cover',
        boxShadow:
          '0 0 0 3px rgba(244, 180, 0, 0.35), 0 8px 28px rgba(0, 0, 0, 0.45)',
      }}
    />
    <Typography component="p" sx={BRAND_TEXT_SX}>
      AC Doctor
    </Typography>
    <Typography
      variant="caption"
      sx={{
        color: '#FFFFFF',
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        fontWeight: 600,
        fontSize: '0.7rem',
        opacity: 0.85,
      }}
    >
      Admin Panel
    </Typography>
  </Box>
);

const Login: React.FC = ({ onLogin }: any) => {
  const [email, setEmail] = useState<string | null>(null);
  const [password, setPassword] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false); // State for password visibility
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>(
    'error',
  );
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isLoggingIn) {
      return;
    }
    if (!email || !password) {
      setSnackbarSeverity('error');
      setSnackbarMessage('Please enter both email and password.');
      setOpenSnackbar(true);
      return;
    }
    setIsLoggingIn(true);
    try {
      const result = await onLogin(email, password);

      if (result?.status) {
        setSnackbarSeverity('success');
        setSnackbarMessage('Login successful!');
        navigate(result.landingPath || '/home');
      } else {
        setSnackbarSeverity('error');
        setSnackbarMessage(
          result?.message || 'Login failed. Please try again.',
        );
      }
    } catch (error: unknown) {
      setSnackbarSeverity('error');
      setSnackbarMessage(
        error instanceof AxiosError
          ? error.message
          : 'An unknown error occurred during login.',
      );
    } finally {
      setIsLoggingIn(false);
      setOpenSnackbar(true);
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <Container
      component="main"
      maxWidth="md"
      sx={{
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Grid
        container
        spacing={2}
        sx={{
          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.5)',
          borderRadius: 3,
          bgcolor: '#1a1a1a',
          border: '1px solid #2a2a2a',
          padding: { xs: 3, md: 4 },
          overflow: 'hidden',
        }}
      >
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: 2,
            py: { xs: 3, md: 4 },
            px: 2,
            background:
              'radial-gradient(ellipse at center, rgba(244, 180, 0, 0.08) 0%, transparent 70%)',
          }}
        >
          <LoginBrand />
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <Typography
            component="h1"
            variant="h4"
            sx={{ color: 'text.primary', mb: 2, fontWeight: 'bold' }}
          >
            Welcome Back!
          </Typography>
          <Typography
            component="p"
            variant="body1"
            sx={{ color: 'text.secondary', mb: 4 }}
          >
            Please enter your login details to access the admin panel.
          </Typography>
          <Box component="form" onSubmit={handleLogin} sx={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email || ''}
              onChange={(e) => setEmail(e.target.value)}
              sx={{ bgcolor: 'grey.900', color: 'white', mb: 2 }}
              InputLabelProps={{ shrink: true, style: { color: 'white' } }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={password || ''}
              onChange={(e) => setPassword(e.target.value)}
              sx={{ bgcolor: 'grey.900', color: 'white', mb: 2 }}
              InputLabelProps={{ shrink: true, style: { color: 'white' } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLoggingIn}
              sx={{
                mt: 3,
                mb: 2,
                bgcolor: BRAND_RED,
                fontWeight: 700,
                py: 1.5,
                '&:hover': { bgcolor: '#c41920' },
                '&:disabled': { bgcolor: '#5c1518', color: '#999' },
              }}
            >
              {isLoggingIn ? 'Signing in…' : 'Continue'}
            </Button>
          </Box>
        </Grid>
      </Grid>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarSeverity}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default withUserAuth(Login);
